(ns frontend.components.inputs.textarea
  (:require
   [cljs-uuid-utils.core :as uuid]
   [common.localization :as loc]
   [frontend.components.inputs.footprint :refer [display-string editor viewer]]
   [frontend.utils :as fu]
   [reagent.core :as r]))

(defn render
  [dom-atom args scroll-calculator]
  (let [{:keys [:value :placeholder :class-name
                :has-focus?
                :on-focus! :on-blur! :on-change!
                :on-key-down! :disabled?]}
        args
        edit-value (or (:temp-value @dom-atom) value)
        render-value (or edit-value value)
        clear-state! (fn []
                       (swap! dom-atom dissoc :temp-value))
        {:keys [show-toggle? show-all?]} @dom-atom]
    [:div.component-textarea__container
     {:class "mb-3"}
     [:p
      {:class-name (fu/cs :component-textarea__autogrow
                          (when show-all?
                            :show-all-textarea))}
      (str render-value " ")] ;; add extra space to ensure new line is rendered if as last char
     [:textarea
      (merge {:ref         (fn [e]
                             (when (and e
                                        (not= e (:input-dom-node @dom-atom)))
                               (swap! dom-atom assoc :input-dom-node e)
                               (.addEventListener e "keyup" scroll-calculator)))
              :placeholder (or placeholder "")
              :class-name  (fu/cs "component-textarea outline-0 focus:border-input-light-border" (when has-focus? "focus") class-name)
              :value       render-value
              :on-change   (fn [e]
                             (let [new-value (-> e
                                                 .-target
                                                 .-value)]
                               (swap! dom-atom assoc :temp-value new-value)
                               (when on-change!
                                 (on-change! e new-value))))
              :on-focus    (fn [e]
                             (.select (:input-dom-node @dom-atom)) ;; all desktop browsers - select all text
                             (when on-focus!
                               (on-focus! e)))
              :on-blur     (fn [e]
                             (when on-blur!
                               (on-blur! e render-value clear-state!)))
              :on-key-down on-key-down!}
             (when disabled?
               {:disabled true}))]
     (when show-toggle?
       [:span.component-textarea__show-all
        {:on-click   (fn [_] (swap! dom-atom update :show-all? not))
         :class-name (fu/cs (if show-all?
                              :component-textarea__show-less
                              :component-textarea__show-all))}
        (loc/tr (if show-all?
                  :show-less
                  :show-all))])]))

(defn create-scroll-calculator
  [component-atom dom-atom]
  (fn [_]
    (let [dom-node (:input-dom-node @dom-atom)
          show-all? (:show-all? @dom-atom)
          scroll-height (or (some->> dom-node
                                     .-scrollHeight)
                            0)
          offset-height (or (some->> dom-node
                                     .-offsetHeight)
                            0)
          max-closed-height (or (:max-closed-height @component-atom) offset-height)
          has-scroll? (< offset-height scroll-height)]      ;; only show when 3px hidden as chrome gives wrong values
      (when (and (not show-all?)
                 has-scroll?)
        (swap! component-atom assoc :max-closed-height offset-height))
      (if (and (not has-scroll?)                         ;does not have scroll
               (or (not show-all?)                       ;; already minimized
                   (< offset-height max-closed-height))) ;; expanded value fits to closed view port
        (swap! dom-atom assoc
               :show-toggle? false
               :show-all? false)
        (swap! dom-atom assoc :show-toggle? true)))))

(defn create-resize-tracker
  [component-atom scroll-calculator]
  (fn [_]
    (let [existing-handle (:resize-handle @component-atom)]
      (when existing-handle
        (js/clearTimeout existing-handle))
      (swap! component-atom assoc
        :resize-handle
        (js/setTimeout (fn [_]
                         (js/clearTimeout (:resize-handle @component-atom))
                         (scroll-calculator nil))
                       ;; you tend to spend some time resizing windows even if you do it seldom -> it's ok to recalculate 5s later
                       5000)))))

(defmethod editor :editor.type/textarea
  [args]
  (let [dom-atom (or (:state-atom args) (r/atom nil))
        component-atom (atom nil)
        component-id (uuid/make-random-uuid)
        window-resize-listener-key (keyword "window-textarea-resize-listener" (uuid/uuid-string component-id))
        set-focusable! (:set-focusable-element! args)
        set-cancel-edit! (:set-cancel-edit! args)
        set-save! (:set-save! args)
        scroll-calculator (create-scroll-calculator component-atom dom-atom)
        resize-tracker (create-resize-tracker component-atom scroll-calculator)]
    ;; ref vs atom
    (r/create-class
     {:display-name           "Textarea"
      :component-did-mount    (fn [_args]
                                ;; we use this to know where focus is changing - we can prevent blur if focus is changing to
                                ;; events withing this editor if needed
                                (when set-focusable!
                                  (set-focusable! (fn [_]
                                                    (when-let [dom-node (:input-dom-node @dom-atom)]
                                                      (.focus dom-node)))))
                                (when set-cancel-edit!
                                  (set-cancel-edit! (fn [_]
                                                      (swap! dom-atom dissoc :temp-value)))) ;; save complete -> reset temp state
                                (when set-save!
                                  (set-save! (fn [_]
                                               (when-let [dom-node (:input-dom-node @dom-atom)]
                                                 (.blur dom-node)))))
                                (swap! dom-atom assoc
                                       window-resize-listener-key
                                       (.addEventListener js/window "resize" resize-tracker))
                                (js/setTimeout scroll-calculator 100))
      :component-did-update   (fn []
                                (js/setTimeout scroll-calculator 1000))
      :component-will-unmount (fn []
                                (.removeEventListener js/window "resize" (get @dom-atom window-resize-listener-key))
                                (swap! dom-atom dissoc :input-dom-node))
      :reagent-render         (fn [args]
                                (render dom-atom args scroll-calculator))})))

(defmethod display-string :editor.type/textarea
  [args]
  (str (:value args)))

(defn render-viewer
  [dom-atom args]
  (let [{:keys [show-toggle? show-all?]} @dom-atom]
    [:div.preview.preview-textarea.component-textarea__container
     [:p
      {:class-name (fu/cs :component-textarea__autogrow
                          :component-textarea-viewer__autogrow
                          (when show-all?
                            :show-all-textarea))}
      (display-string args)]
     (when show-toggle?
       [:span.component-textarea__show-all
        {:on-click   (fn [_] (swap! dom-atom update :show-all? not))
         :class-name (fu/cs (if show-all?
                              :component-textarea__show-less
                              :component-textarea__show-all))}
        (loc/tr (if show-all?
              :show-less
              :show-all))])]))

(defmethod viewer :editor.type/textarea
  [args]
  (let [dom-atom (or (:state-atom args) (r/atom nil))
        component-atom (atom nil)
        component-id (uuid/make-random-uuid)
        window-resize-listener-key (keyword "window-textarea-viewer-resize-listener" (uuid/uuid-string component-id))
        scroll-calculator (create-scroll-calculator component-atom dom-atom)
        resize-tracker (create-resize-tracker component-atom scroll-calculator)]
    (r/create-class
     {:display-name           "textarea/viewer"
      :component-did-mount    (fn [_args]
                                (swap! dom-atom assoc
                                       window-resize-listener-key
                                       (.addEventListener js/window "resize" resize-tracker))
                                (js/setTimeout scroll-calculator 100))
      :component-did-update   (fn []
                                (js/setTimeout scroll-calculator 1000))
      :component-will-unmount (fn []
                                (.removeEventListener js/window "resize" (get @dom-atom window-resize-listener-key)))
      :reagent-render         (fn [args]
                                (render-viewer dom-atom args))})))
